/**
 * ShareService — "Share BattleBrew" support for the landing page.
 *
 * Pure, unit-testable helpers behind the Share overlay (index.html
 * `#share-overlay`, wired in src/main.ts). Sharing is 100% client-side —
 * no server involvement: the browser is pointed at each platform's
 * share-intent page and the player posts it themselves.
 *
 * Conventions:
 *  - Every platform WITH a documented web share intent gets an intent
 *    URL, opened in a new window via `openShareWindow` (noopener).
 *  - Platforms WITHOUT a documented web intent (TikTok) fall back to
 *    "copy the caption + open the site" — the caption goes to the
 *    clipboard and the player pastes it into a new post.
 *  - The overlay's Copy button uses `buildShareCaption` (tagline + URL).
 *  - Every URL is built with encodeURIComponent — the app URL may carry
 *    query params (e.g. `?server=...`), so raw concatenation is forbidden.
 *
 * APPENDING A NEW PLATFORM: add it to SHARE_TARGETS (keep the array
 * order — the overlay renders in this order). tests/shareService.test.ts
 * pins the current set of ids.
 */

/** Title used in platforms that take a title (Reddit). */
export const SHARE_APP_TITLE = 'BattleBrew — a free-to-play browser auto-battler';

/** Short tagline used in the shared caption (tagline + app URL). */
export const SHARE_APP_TAGLINE =
  'BattleBrew: a free-to-play browser auto-battler. No download, no install — just click and queue up!';

export type ShareTargetId =
  | 'reddit'
  | 'facebook'
  | 'x'
  | 'whatsapp'
  | 'linkedin'
  | 'tiktok';

export interface ShareTarget {
  id: ShareTargetId;
  label: string;
  icon: string;
  /**
   * The platform's web share-intent URL for the app, or null when the
   * platform has no documented web intent (TikTok — the caller then
   * copies the caption and opens `fallbackOpenUrl`).
   */
  buildShareUrl: (appUrl: string) => string | null;
  /** Site opened for intent-less targets (TikTok). */
  fallbackOpenUrl: string | null;
}

/**
 * The share catalogue — rendered IN ORDER by the Share overlay.
 * APPEND new platforms at the end (tests pin this set).
 */
export const SHARE_TARGETS: readonly ShareTarget[] = [
  {
    id: 'reddit',
    label: 'Reddit',
    icon: '🤖',
    buildShareUrl: (appUrl) =>
      `https://www.reddit.com/submit?url=${encodeURIComponent(appUrl)}` +
      `&title=${encodeURIComponent(SHARE_APP_TITLE)}`,
    fallbackOpenUrl: null,
  },
  {
    id: 'facebook',
    label: 'Facebook',
    icon: '📘',
    buildShareUrl: (appUrl) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(appUrl)}`,
    fallbackOpenUrl: null,
  },
  {
    id: 'x',
    label: 'X (Twitter)',
    icon: '🐦',
    buildShareUrl: (appUrl) =>
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(appUrl)}` +
      `&text=${encodeURIComponent(`${SHARE_APP_TITLE} ${appUrl}`)}`,
    fallbackOpenUrl: null,
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    icon: '💬',
    buildShareUrl: (appUrl) =>
      `https://wa.me/?text=${encodeURIComponent(buildShareCaption(appUrl))}`,
    fallbackOpenUrl: null,
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    icon: '💼',
    buildShareUrl: (appUrl) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(appUrl)}`,
    fallbackOpenUrl: null,
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    icon: '🎵',
    // TikTok has no documented WEB share intent — the caller copies the
    // caption (clipboard) and opens the site for a manual post.
    buildShareUrl: () => null,
    fallbackOpenUrl: 'https://www.tiktok.com/',
  },
];

/** Look up a share target by id (undefined for unknown ids). */
export function getShareTarget(id: ShareTargetId): ShareTarget | undefined {
  return SHARE_TARGETS.find((t) => t.id === id);
}

/**
 * The share caption (tagline + app URL) — used by the WhatsApp intent,
 * the TikTok caption-copy fallback, and the overlay's Copy button.
 */
export function buildShareCaption(appUrl: string): string {
  return `${SHARE_APP_TAGLINE} ${appUrl}`;
}

/**
 * Copy text to the clipboard.
 *
 * Tries `navigator.clipboard.writeText` first (secure contexts only) and
 * falls back to a hidden textarea + `document.execCommand('copy')`
 * (legacy browsers / non-secure contexts). Returns true on success.
 * Reads the globals via `globalThis` so the function stays importable
 * from non-DOM (Node/jest) contexts for unit testing.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  const nav: { clipboard?: { writeText?: (t: string) => Promise<void> } } | undefined =
    (globalThis as { navigator?: { clipboard?: { writeText?: (t: string) => Promise<void> } } })
      .navigator;
  if (nav?.clipboard?.writeText) {
    try {
      await nav.clipboard.writeText(text);
      return true;
    } catch {
      // Clipboard rejected (e.g. permission) — fall through to the
      // textarea path.
    }
  }
  const doc:
    | {
        createElement: (tag: string) => {
          value: string;
          style: Record<string, string>;
          focus: () => void;
          select: () => void;
        };
        body: { appendChild: (el: unknown) => void; removeChild: (el: unknown) => void };
        execCommand: (cmd: string) => boolean;
      }
    | undefined = globalThis.document as typeof doc | undefined;
  if (!doc) return false;
  const textarea = doc.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  doc.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  let ok = false;
  try {
    ok = doc.execCommand('copy');
  } catch {
    ok = false;
  }
  doc.body.removeChild(textarea);
  return ok;
}

/**
 * Open a share-intent URL in a new window (noopener — the share dialog
 * must not gain control of our page). Returns the window handle or null
 * (e.g. popup blocked, or no window global in non-DOM test contexts).
 */
export function openShareWindow(url: string): Window | null {
  const win: { open?: (url: string, target: string, features?: string) => Window | null } | undefined =
    globalThis.window as typeof win | undefined;
  if (!win?.open) return null;
  return win.open(url, '_blank', 'noopener,noreferrer');
}
